/*
 This file is part of GNU Taler
 (C) 2021-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

/**
 *
 * @author Sebastian Javier Marchano (sebasjm)
 */

import {
  AmountString,
  Amounts,
  Duration,
  HttpStatusCode,
  TalerError,
  TalerMerchantApi,
  TalerProtocolDuration,
  TemplateType,
  TranslatedString,
  assertUnreachable,
} from "@gnu-taler/taler-util";
import {
  ButtonBetterBulma,
  LocalNotificationBannerBulma,
  NotificationCardBulma,
  undefinedIfEmpty,
  useLocalNotificationBetter,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { Fragment, VNode, h } from "preact";
import { useState } from "preact/hooks";
import {
  FormErrors,
  FormProvider,
} from "../../../../components/form/FormProvider.js";
import { Input } from "../../../../components/form/Input.js";
import { InputDurationDropdown } from "../../../../components/form/InputDurationDropdown.js";
import { InputNumber } from "../../../../components/form/InputNumber.js";
import { InputSelector } from "../../../../components/form/InputSelector.js";
import { InputToggle } from "../../../../components/form/InputToggle.js";
import { InputWithAddon } from "../../../../components/form/InputWithAddon.js";
import { TextField } from "../../../../components/form/TextField.js";
import {
  ComponentPersonaFlag,
  FragmentPersonaFlag,
} from "../../../../components/menu/SideBar.js";
import { Tooltip } from "../../../../components/Tooltip.js";
import { useSessionContext } from "../../../../context/session.js";
import { WithId } from "../../../../declaration.js";
import { useInstanceOtpDevices } from "../../../../hooks/otp.js";
import { UIElement } from "../../../../hooks/preference.js";

const TALER_SCREEN_ID = 65;

type Entity = {
  description?: string;
  otpId?: string | null;
  currency: string;
  summary?: string;
  amount?: string;
  minimum_age?: number;
  pay_duration?: Duration;
  summary_editable?: boolean;
  amount_editable?: boolean;
  currency_editable?: boolean;
};

interface Props {
  onUpdated: () => void;
  onBack?: () => void;
  template: TalerMerchantApi.TemplateDetails & WithId;
}

function changeToCurrency(
  am: string | undefined,
  currency: string | undefined,
): string | undefined {
  if (!am || !currency) return undefined;
  const amount = Amounts.parse(am);
  if (!amount) return undefined;
  const newAmount = {
    ...amount,
    currency,
  };
  return Amounts.stringify(newAmount);
}

function UpdateFixedOrderPage({ template, onUpdated, onBack }: Props): VNode {
  const { i18n } = useTranslationContext();
  const { config, state: session, lib } = useSessionContext();
  const [notification, safeFunctionHandler] = useLocalNotificationBetter();

  if (template.template_contract.template_type !== TemplateType.FIXED_ORDER) {
    return <Fragment />
  }
  
  const cList = Object.values(config.currencies).map((d) => d.name);
  const supportedCurrencies = Object.keys(config.currencies);
  const currentAmount =
    template.editable_defaults?.amount ??
    (template.template_contract.amount as AmountString | undefined);

  const default_currency = supportedCurrencies[0];

  const template_currency = currentAmount
    ? Amounts.currencyOf(currentAmount)
    : default_currency;

  const unsupportedCurrency =
    supportedCurrencies.indexOf(template_currency) === -1;

  const startingAmount = unsupportedCurrency
    ? changeToCurrency(currentAmount, default_currency)
    : currentAmount;

  const [state, setState] = useState<Partial<Entity>>({
    description: template.template_description,
    minimum_age: template.template_contract.minimum_age,
    otpId: template.otp_id,
    pay_duration: template.template_contract.pay_duration
      ? Duration.fromTalerProtocolDuration(
          template.template_contract.pay_duration,
        )
      : undefined,
    summary:
      template.editable_defaults?.summary ?? template.template_contract.summary,
    currency: unsupportedCurrency ? default_currency : template_currency,
    amount: startingAmount,
    currency_editable: !!template.editable_defaults?.currency,
    summary_editable: template.editable_defaults?.summary !== undefined,
    amount_editable: template.editable_defaults?.amount !== undefined,
  });

  function updateState(up: (s: Partial<Entity>) => Partial<Entity>) {
    setState((old) => {
      const newState = up(old);
      if (!newState.amount_editable) {
        newState.currency_editable = false;
      }
      newState.amount = changeToCurrency(newState.amount, newState.currency);
      return newState;
    });
  }

  const devices = useInstanceOtpDevices();
  const deviceList =
    !devices || devices instanceof TalerError || devices.type === "fail"
      ? []
      : devices.body.otp_devices;
  const deviceMap = deviceList.reduce(
    (prev, cur) => {
      prev[cur.otp_device_id] = cur.device_description as TranslatedString;
      return prev;
    },
    {} as Record<string, TranslatedString>,
  );

  const parsedPrice = !state.amount ? undefined : Amounts.parse(state.amount);

  const errors = undefinedIfEmpty<FormErrors<Entity>>({
    description: !state.description ? i18n.str`Required` : undefined,
    summary: !state.summary
      ? state.summary_editable
        ? undefined
        : i18n.str`Required`
      : undefined, // more summary validations? none
    amount: !state.amount
      ? state.amount_editable
        ? undefined
        : i18n.str`Required`
      : !parsedPrice // more summary validations? is valid amount...
        ? i18n.str`Invalid`
        : Amounts.isZero(parsedPrice)
          ? state.amount_editable
            ? undefined
            : i18n.str`Must be greater than 0`
          : undefined,
    minimum_age:
      state.minimum_age && state.minimum_age < 0
        ? i18n.str`Must be greater that 0`
        : undefined,
    pay_duration: !state.pay_duration
      ? i18n.str`Required`
      : state.pay_duration.d_ms === "forever"
        ? undefined
        : state.pay_duration.d_ms < 1000 // less than one second
          ? i18n.str`Too short`
          : undefined,
  });

  const zero = Amounts.stringify(
    Amounts.zeroOfCurrency(state.currency ?? default_currency),
  );

  const contract_amount = state.amount_editable
    ? undefined
    : (state.amount as AmountString);
  const contract_summary = state.summary_editable ? undefined : state.summary;
  const template_contract: TalerMerchantApi.TemplateContractDetails = {
    template_type: TemplateType.FIXED_ORDER,
    minimum_age: state.minimum_age!,
    pay_duration: state.pay_duration
      ? Duration.toTalerProtocolDuration(state.pay_duration)
      : TalerProtocolDuration.forever(),
    amount: contract_amount,
    summary: contract_summary,
    currency:
      cList.length > 1 && state.currency_editable ? undefined : state.currency,
  };
  const data: TalerMerchantApi.TemplatePatchDetails = {
    template_description: state.description!,
    template_contract,
    editable_defaults: {
      amount: !state.amount_editable ? undefined : state.amount ?? zero,
      summary: !state.summary_editable ? undefined : state.summary ?? "",
      currency:
        cList.length === 1 || !state.currency_editable
          ? undefined
          : state.currency,
    },
    otp_id: state.otpId!,
  };
  const update = safeFunctionHandler(
    i18n.str`update template`,
    lib.instance.updateTemplate.bind(lib.instance),
    !session.token || !!errors ? undefined : [session.token, template.id, data],
  );
  update.onSuccess = onUpdated;
  update.onFail = (fail) => {
    switch (fail.case) {
      case HttpStatusCode.Unauthorized:
        return i18n.str`Unauthorized`;
      case HttpStatusCode.NotFound:
        return i18n.str`Not found`;
      case HttpStatusCode.Conflict:
        return i18n.str`Conflict`;
      default:
        assertUnreachable(fail);
    }
  };

  return (
    <div>
      <LocalNotificationBannerBulma notification={notification} />
      <section class="section">
        <section class="hero is-hero-bar">
          <div class="hero-body">
            <div class="level">
              <div class="level-left">
                <div class="level-item">
                  <span class="is-size-4">
                    {
                      new URL(
                        `templates/${template.id}`,
                        session.backendUrl.href,
                      ).href
                    }
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>
        <hr />
        {unsupportedCurrency ? (
          <NotificationCardBulma
            notification={{
              message: i18n.str`The template configuration needs to be fixed.`,
              description: i18n.str`The currency of the template is ${template_currency} and is not in the list of supported currencies.`,
              type: "WARN",
            }}
          />
        ) : undefined}

        <section class="section is-main-section">
          <div class="columns">
            <div class="column is-four-fifths">
              <FormProvider
                object={state}
                valueHandler={updateState}
                errors={errors}
              >
                <Input<Entity>
                  name="description"
                  label={i18n.str`Template name`}
                  tooltip={i18n.str`Describe what this template stands for`}
                />
                <Input<Entity>
                  name="summary"
                  inputType="multiline"
                  label={i18n.str`Order summary`}
                  tooltip={i18n.str`If specified, this template will create order with the same summary`}
                />
                <InputToggle<Entity>
                  name="summary_editable"
                  label={i18n.str`Summary is editable`}
                  tooltip={i18n.str`Allow the user to change the summary.`}
                />
                {supportedCurrencies.length > 1 ? (
                  <InputSelector
                    name="currency"
                    label={i18n.str`Supported currencies`}
                    values={supportedCurrencies}
                  />
                ) : undefined}

                <InputWithAddon<Entity>
                  name="amount"
                  label={i18n.str`Amount`}
                  addonBefore={state.currency}
                  inputType="decimal"
                  toStr={(v?: AmountString) => v?.split(":")[1] || ""}
                  fromStr={(v: string) =>
                    !v ? undefined : `${state.currency}:${v}`
                  }
                  inputExtra={{ min: 0, step: 0.001 }}
                  tooltip={i18n.str`If specified, this template will create orders with the same price`}
                />
                <InputToggle<Entity>
                  name="amount_editable"
                  label={i18n.str`Amount is editable`}
                  tooltip={i18n.str`Allow the user to select the amount to pay.`}
                />
                {cList.length > 1 && (
                  <Fragment>
                    <InputToggle<Entity>
                      name="currency_editable"
                      readonly={!state.amount_editable}
                      label={i18n.str`Currency is editable`}
                      tooltip={i18n.str`Allow the user to change currency.`}
                    />
                  </Fragment>
                )}
                <FragmentPersonaFlag point={UIElement.option_ageRestriction}>
                  <InputNumber<Entity>
                    name="minimum_age"
                    label={i18n.str`Minimum age`}
                    tooltip={i18n.str`Is this contract restricted to some age?`}
                  />
                </FragmentPersonaFlag>
                <FragmentPersonaFlag
                  point={UIElement.option_paymentTimeoutOnTemplate}
                >
                  <InputDurationDropdown<Entity>
                    name="pay_duration"
                    label={i18n.str`Payment timeout`}
                    help=""
                    tooltip={i18n.str`How much time has the customer to complete the payment once the order was created.`}
                    withoutClear
                  />
                </FragmentPersonaFlag>

                {!deviceList.length ? (
                  <ComponentPersonaFlag
                    Comp={TextField}
                    point={UIElement.option_otpDevicesOnTemplate}
                    name="otpId"
                    label={i18n.str`OTP device`}
                    tooltip={i18n.str`Use to verify transactions in offline mode.`}
                  >
                    <i18n.Translate>No OTP device.</i18n.Translate>&nbsp;
                    <a href="#/otp-devices/new">
                      <i18n.Translate>Add one first</i18n.Translate>
                    </a>
                  </ComponentPersonaFlag>
                ) : (
                  <InputSelector<Entity>
                    name="otpId"
                    label={i18n.str`OTP device`}
                    values={[
                      undefined,
                      ...deviceList.map((e) => e.otp_device_id),
                    ]}
                    toStr={(v?: string) => {
                      if (!v) {
                        return i18n.str`No device`;
                      }
                      return deviceMap[v];
                    }}
                    tooltip={i18n.str`Use to verify transactions in offline mode.`}
                  />
                )}
                <div class="buttons is-right mt-5">
                  {onBack && (
                    <button type="button" class="button" onClick={onBack}>
                      <i18n.Translate>Cancel</i18n.Translate>
                    </button>
                  )}
                  <Tooltip
                    text={
                      errors !== undefined
                        ? i18n.str`Please complete the marked fields`
                        : i18n.str`Confirm operation`
                    }
                  >
                    <ButtonBetterBulma type="submit" onClick={update}>
                      <i18n.Translate>Confirm</i18n.Translate>
                    </ButtonBetterBulma>
                  </Tooltip>
                </div>
              </FormProvider>
            </div>
          </div>
        </section>
      </section>
    </div>
  );
}
function UpdatePaivanaPage({ template, onUpdated, onBack }: Props): VNode {
  return <div>unsupported paivana template</div>
}
function UpdateInventoryPage({ template, onUpdated, onBack }: Props): VNode {
  return <div>unsupported inventory template</div>
}

export function UpdatePage(props: Props): VNode {
  switch (props.template.template_contract.template_type) {
    case TalerMerchantApi.TemplateType.FIXED_ORDER:
      return UpdateFixedOrderPage(props);
    case TalerMerchantApi.TemplateType.INVENTORY_CART:
      return UpdateInventoryPage(props);
    case TalerMerchantApi.TemplateType.PAIVANA:
      return UpdatePaivanaPage(props);
    default:
      assertUnreachable(props.template.template_contract);
  }
}
